import { apiClient } from './api';
import { ApiResponse } from '../types/api';

export interface BookingWorkflowExitRequest {
  conversationId: string;
  workflowSessionId: string;
  agent?: string;
  guestEmail: string;
  selectedReservation?: {
    reservationId: string;
    confirmationId: string;
    roomTypeCode?: string;
    arrivalDate?: string;
    departureDate?: string;
  };
}

export interface BookingWorkflowExitResponse {
  success: boolean;
  message?: string;
  /** Backend-reported exit outcome: 'completed' = workflow finished successfully
   *  (session status 'completed'); 'cancelled' = abandoned/incomplete;
   *  'noop' = nothing actionable. Undefined on older backends. */
  outcome?: 'completed' | 'cancelled' | 'noop';
}


export interface UpdateCheckInTimeRequest {
  reservationId: string;
  arrivalDate: string;
  departureDate: string;
  newCheckInTime: string;
  workflowSessionId: string;
  conversationId: string;
  agent: string;
}

export interface UpdateCheckInTimeResponse {
  reservationId?: string;
  confirmationId?: string;
  oldCheckInTime?: string;
  newCheckInTime?: string;
  arrivalDate?: string;
  arrivalDateTime?: string;
  departureDate?: string;
  workflowSessionId?: string;
  conversationId?: string;
  // Kept for legacy callers / error paths.
  success?: boolean;
  message?: string;
}

export interface ModifyBookingReservationRequest {
  reservationId?: string;
  workflowSessionId: string;
  conversationId: string;
  email: string;
  agentName: string;
}

export interface ModifyBookingReservationDetails {
  id: string;
  reservationId: string;
  confirmationId: string;
  status: string;
  guestName: string;
  firstName: string;
  lastName: string;
  email: string;
  guestProfileId: string;
  arrivalDate: string;
  departureDate: string;
  numberOfNights: number;
  adultCount: number;
  childrenCount: number;
  numberOfRooms: number;
  roomType: string;
  roomTypeCode: string;
  roomName: string;
  roomMedia: Array<{
    alt: string;
    url: string;
    tags: string[];
    bestFor: string[];
    caption: string;
    imageId: string;
    category: string;
    roomType: string[];
    isPrimary: boolean;
  }>;
  rateCode: string;
  rateAmount: string;
  currencyCode: string;
  marketCode: string;
  sourceCode: string;
  paymentMethod: string;
  createdAt: string;
  updatedAt: string;
  // Previous stay information for room preferences
  mostRecentReservation?: {
    id?: string;
    reservationId?: string;
    confirmationId?: string;
    reservationStatus?: string;
    arrivalDate?: string;
    departureDate?: string;
    roomTypeCode?: string;
    roomTypeName?: string;
    roomNumber?: string;
    adults?: number;
    children?: number;
    createdAt?: string;
    updatedAt?: string;
  };
  // Cancellation policy from Opera Cloud
  cancellationPolicy?: {
    revenueType?: string;
    policy?: {
      deadline?: { absoluteDeadline?: string; };
      amountPercent?: {
        basisType?: string;
        nights?: number;
        percent?: number;
        amount?: number;
      };
      policyCode?: string;
      manual?: boolean;
      effective?: boolean;
    };
    percentageDue?: number;
    comments?: string;
    policyId?: { id?: string; type?: string; };
  };
  // Deposit policy from Opera Cloud
  depositPolicy?: {
    revenueType?: string;
    policy?: {
      amountPercent?: {
        basisType?: string;
        percent?: number;
        amount?: number;
      };
      deadline?: { absoluteDeadline?: string; };
      depositReqReversed?: boolean;
      policyCode?: string;
      manual?: boolean;
    };
    comments?: string;
    amountPaid?: { amount?: number; };
    amountDue?: { amount?: number; };
    policyId?: { id?: string; type?: string; };
  };
}

export interface ModifyBookingUserContext {
  workflowSessionId: string;
  conversationId: string;
  userMessage: string;
  createdAt: string;
  currentStep: string;
}

export interface ModifyBookingResponse {
  success: boolean;
  message: string;
  data: {
    reservations: ModifyBookingReservationDetails[];
    totalReservations: number;
    userContext: ModifyBookingUserContext;
    workflowActive: boolean;
  };
}

export interface RoomTypeMedia {
  url: string;
  alt: string;
}

export interface RoomType {
  roomCode: string;
  roomName: string;
  description: string;
  roomMedia: RoomTypeMedia[];
  maxOccupancy: number;
  roomSizeSqft: number;
  bedType: string;
  numberOfBeds: number;
  roomAmenities: string;
  roomView: string;
  featuredImageUrl: string;
  imageUrls: string[];
  smokingAllowed: boolean;
}

export interface ModifyBookingRoomTypesResponse {
  success: boolean;
  message: string;
  data: {
    roomTypes: RoomType[];
    totalRoomTypes: number;
  };
}

export interface GetRoomNumbersRequest {
  roomType: string;
}

export interface RoomNumber {
  roomNumber: string;
  roomType: string;
  roomTypeCode: string;
  isAvailable: boolean;
}

// Updated interfaces to match the actual API response structure
export interface ApiRoomData {
  roomType?: {
    pseudo?: boolean;
    roomClass?: string;
    houseKeeping?: boolean;
    roomType?: string;
  };
  roomId: string;
  housekeeping?: {
    roomStatus?: {
      roomStatus?: string;
      frontOfficeStatus?: string;
    };
  };
}

export interface GetRoomNumbersApiResponse {
  success: boolean;
  data?: {
    hotelRoomsDetails?: {
      room?: ApiRoomData[];
      hotelId?: string;
    };
    totalPages?: number;
    offset?: number;
    limit?: number;
    hasMore?: boolean;
    totalResults?: number;
    links?: any[];
  } | ApiRoomData[] | { room?: ApiRoomData[] };
  hotelRoomsDetails?: {
    room?: ApiRoomData[];
    hotelId?: string;
  };
  room?: ApiRoomData[];
}

export interface GetRoomNumbersResponse {
  success: boolean;
  message?: string;
  data?: {
    roomNumbers: RoomNumber[];
    totalRooms: number;
    availableRooms: number;
    roomType: string;
  };
  error?: {
    message: string;
    code?: string;
  };
}

export interface ModifyBookingPaymentRequest {
  roomCode: string;
  ratePlanCode?: string;
  conversationId: string;
  workflowSessionId: string;
  arrivalDate?: string;
  departureDate?: string;
  adults?: number;
  children?: number;
  numberOfRooms?: number;
  agent?: string;
  previousRoomCode?: string;
  guestEmail?: string;
  reservationId?: string;
}

export interface ModifyBookingPaymentResponse {
  success: boolean;
  message?: string;
  error?: {
    message: string;
  };
  data?: {
    roomCode: string;
    roomType: {
      name: string;
      description: string;
      maxOccupancy: number;
      roomSize: string | null;
      bedType: string;
    };
    roomMedia: Array<{
      alt: string;
      url: string;
      tags: string[];
      bestFor: string[];
      caption: string;
      imageId: string;
      category: string;
      roomType: string[];
      isPrimary: boolean;
    }>;
    paymentMethods: any[];
    taxTypes: any[];
    userBookingDetails: any;
    cancellationPolicy: any;
    totalAmount: number;
    taxAmount: number;
    resortFee: number;
    finalAmount: number;
    cards?: Array<{
      id: string;
      cardType: string;
      last4: string;
      cardholderName: string;
      expiryMonth: string;
      expiryYear: string;
      isDefault?: boolean;
    }>;
    savedCards?: Array<{
      id: string;
      cardType: string;
      last4: string;
      cardholderName: string;
      expiryMonth: string;
      expiryYear: string;
      isDefault?: boolean;
    }>;
  };
}

export interface ModifyReservationRequest {
  reservationId: string;
  selectedRoomCode: string;
  arrivalDate: string;
  departureDate: string;
  numberOfRooms: number;
  adults?: number;
  children?: number;
  workflowSessionId: string;
  conversationId: string;
  agent?: string;
  paymentMethodToken?: string;
  paymentType?: string;
}

export interface ModifyReservationResponse {
  success: boolean;
  message: string;
  data: {
    reservationId: string;
    newRoomType: string;
    newArrivalDate: string;
    newDepartureDate: string;
    numberOfNights: number;
    newRateAmount: number;
    currencyCode: string;
    workflowSessionId: string;
    conversationId: string;
  };
}

export interface UpdateCheckoutTimeRequest {
  reservationId: string;
  arrivalDate: string;
  departureDate: string;
  newCheckOutTime: string;
  workflowSessionId: string;
  conversationId: string;
  agent: string;
  guestEmail?: string;
}

export interface UpdateCheckoutTimeResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId: string;
    newCheckOutTime: string;
    arrivalDate: string;
    departureDate: string;
  };
  error?: {
    message: string;
    code?: string;
  };
}

export interface BreakfastPackage {
  packageCode: string;
  description: string;
  unitPrice: number;
  totalGuestsNeeded: number;
  totalNights: number;
  totalUnits: number;
  totalCost: number;
  schedule: {
    schedulePrices: Array<{
      unitPrice: number;
      bucket: string;
    }>;
    start: string;
    end: string;
  };
}

export interface BreakfastPricing {
  numberOfNights: number;
  totalGuests: number;
  adultCount: number;
  childrenCount: number;
  packages: BreakfastPackage[];
  totalCost: number;
  currencyCode: string;
}

export interface BreakfastIncludedResponse {
  success: boolean;
  message?: string;
  breakfastAnalysis: {
    included: boolean;
    adultCount: number;
    childrenCount: number;
    adultBreakfastQuantity: number;
    childBreakfastQuantity: number;
    breakfastDetails: any[];
    coverageStatus: string;
  };
  workflowSessionId: string;
  conversationId: string;
  breakfastPricing?: BreakfastPricing;
  ohipData?: {
    reservations: {
      reservation: Array<{
        reservationIdList: Array<{
          id: string;
          type: string;
        }>;
        roomStay: {
          arrivalDate: string;
          departureDate: string;
          expectedTimes: {
            reservationExpectedArrivalTime: string;
            reservationExpectedDepartureTime: string;
          };
          guarantee: {
            guaranteeCode: string;
            shortDescription: string;
          };
        };
        reservationGuests: Array<{
          profileInfo: {
            profileIdList: Array<{
              id: string;
              type: string;
            }>;
            profile: {
              customer: {
                personName: Array<{
                  givenName: string;
                  surname: string;
                  nameType: string;
                }>;
              };
            };
          };
          primary: boolean;
        }>;
        extSystemSync: boolean;
        hotelId: string;
        reservationStatus: string;
        createDateTime: string;
        preRegistered: boolean;
        allowMobileCheckout: boolean;
      }>;
    };
    masterInfoList: any[];
    links: Array<{
      href: string;
      rel: string;
      templated: boolean;
      method: string;
      operationId: string;
    }>;
  };
}

export interface AddBreakfastPackageRequest {
  conversationId: string;
  workflowSessionId: string;
  breakfastCodes: string[];
  adultCount: number;
  childrenCount: number;
}

export interface AddBreakfastPackageResponse {
  success: boolean;
  message: string;
  data?: {
    reservationId: string;
    packagesAdded: number;
    totalCost: number;
    packages: Array<{
      code: string;
      description: string;
      quantity: string;
      nights: number;
      unitPrice: number;
    }>;
  };
  workflowSessionId: string;
  conversationId: string;
  error?: string;
}

export interface UpgradeUpsellRequest {
  numberOfRooms: number;
  roomType: string;
  startDate: string;
  endDate: string;
  adults: number;
  children: number;
  reservationId: string;
  conversationId: string;
  workflowSessionId: string;
}

export interface RoomFeature {
  code: string;
  description: string;
  orderSequence: number;
}

export interface UpsellOffer {
  ruleId: {
    id: string;
    type: string;
  };
  ruleCode: string;
  ruleDescription: string;
  percentageSavings: number;
  ratePlanCode: string;
  roomType: {
    longDescription: string;
    roomFeatures: RoomFeature[];
    roomTypeAttributes: {
      maximumOccupancy: number;
    };
    roomType: string;
    hotelId: string;
    roomClass: string;
    description: string;
    suite: boolean;
    component: boolean;
    roomMedia?: Array<{
      alt: string;
      url: string;
      tags: string[];
      bestFor: string[];
      caption: string;
      imageId: string;
      category: string;
      roomType: string[];
      isPrimary: boolean;
    }>;
    taxAmount?: number;
    taxPercentage?: number;
    finalAmount?: number;
  };
  firstNightAmount: {
    amount: {
      amount: number;
    };
  };
  totalStayAmount: {
    amount: {
      amount: number;
    };
  };
  rates: Array<{
    date: string;
    requiredAmount: {
      amount: {
        amount: number;
      };
    };
  }>;
}

export interface UpgradeUpsellResponse {
  upsells: {
    upsell: UpsellOffer[];
  };
}

export interface UpgradeRoomRequest {
  reservationId: string;
  adults: number;
  children: number;
  startDate: string;
  endDate: string;
  previousRoomCode: string;
  roomCode: string;
  conversationId?: string;
  workflowSessionId?: string;
}

export interface UpgradeRoomResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId: string;
    previousRoomCode: string;
    newRoomCode: string;
    upgradeConfirmed: boolean;
    additionalCharges?: number;
  };
}

export interface CheckInRequest {
  reservationId: string;
  conversationId?: string;
  workflowSessionId?: string;
  paymentMethodToken?: string; // Add payment method token for check-in API
}

export interface CheckInResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId: string;
    status: string;
    ohipResponse: any;
  };
}

export interface GetAvailableRoomNumbersRequest {
  roomTypeCode: string;
  startDate: string; // YYYY-MM-DD
  endDate: string; // YYYY-MM-DD
}

export interface RoomFeature {
  code: string;
  description: string;
}

export interface RoomMedia {
  images: Array<{
    url: string;
    alt: string;
    isPrimary: boolean;
  }>;
  virtualTour: string | null;
  roomTypeName: string;
  roomTypeDescription: string;
}

export interface AvailableRoom {
  roomId: string;
  floor: string;
  floorDescription: string;
  roomFeatures: RoomFeature[];
  smokingPreference: string;
  smokingPreferenceDescription: string;
  roomMedia: RoomMedia;
  lastStayDate: string | null;
  vacantDays: number;
}

export interface GetAvailableRoomNumbersResponse {
  success: boolean;
  message?: string;
  data?: {
    rooms: AvailableRoom[];
    roomTypeCode: string;
    roomTypeName: string;
    totalAvailable: number;
    showing: number;
    startDate: string;
    endDate: string;
  };
}


/**
 * Service for handling booking workflow operations
 */
class BookingService {
  /**
   * Exit the booking workflow
   * Called when user cancels, closes modal, or navigates away from booking process
   */
  async exitWorkflow(hotelId: string, request: BookingWorkflowExitRequest): Promise<BookingWorkflowExitResponse> {
    try {
      console.log('🚪 Exiting booking workflow:', { hotelId, ...request });
      
      const response: any = await apiClient.post<BookingWorkflowExitResponse>(
        `/tenants/${hotelId}/booking/workflow/exit`,
        request
      );

      // apiClient returns the backend body directly when it carries `success`,
      // so `message` / `outcome` live on `response` itself; fall back to
      // `response.data` in case a wrapped envelope is returned instead.
      const message = response?.message ?? response?.data?.message;
      let outcome: BookingWorkflowExitResponse['outcome'] =
        response?.outcome ?? response?.data?.outcome;

      // Fallback for backends that don't yet return the `outcome` field: derive
      // it from the known exit messages. 'completed' is the only "stay" case;
      // everything else (abandoned / incomplete) is treated as a cancel.
      if (!outcome && typeof message === 'string') {
        outcome = /after completion/i.test(message) ? 'completed' : 'cancelled';
      }

      if (response?.success) {
        console.log('✅ Booking workflow exit successful:', { message, outcome });
        return {
          success: true,
          message: message || 'Booking workflow exit successful',
          outcome,
        };
      } else {
        console.warn('⚠️ Booking workflow exit failed:', response?.error);
        const errorMessage = response?.error?.message || message || 'Failed to exit workflow';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to exit booking workflow:', error);
      throw error;
    }
  }

  /**
   * Safe wrapper that won't throw errors - useful for cleanup operations
   * where we don't want to block the user's action if the API call fails
   */
  async exitWorkflowSafe(hotelId: string, request: BookingWorkflowExitRequest): Promise<boolean> {
    try {
      const response = await this.exitWorkflow(hotelId, request);
      return response.success;
    } catch (error) {
      console.error('❌ Silent failure in booking workflow exit:', error);
      return false;
    }
  }

  /**
   * Exit all active workflows for a user
   * Called when user initially loads the dashboard to clean up any lingering workflows
   */
  async exitAllWorkflows(hotelId: string, email: string): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('🚪 Exiting all workflows for user:', { hotelId, email });
      
      const response = await apiClient.post<{ success: boolean; message?: string }>(
        `/tenants/${hotelId}/booking/workflow/exit-all`,
        {
          email
        }
      );
      
      if (response.success) {
        console.log('✅ All workflows exited successfully:', response.data);
        const exitAllResponse = {
          success: response.success,
          message: (response as any).message || response.data?.message || 'All workflows exited successfully'
        };
        return exitAllResponse;
      } else {
        console.warn('⚠️ Exit all workflows failed:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to exit all workflows';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to exit all workflows:', error);
      throw error;
    }
  }

  /**
   * Safe wrapper for exitAllWorkflows that won't throw errors
   * Useful for cleanup operations where we don't want to block the user's experience
   */
  async exitAllWorkflowsSafe(hotelId: string, email: string): Promise<boolean> {
    try {
      const response = await this.exitAllWorkflows(hotelId, email);
      return response.success;
    } catch (error) {
      console.error('❌ Silent failure in exit all workflows:', error);
      return false;
    }
  }

  /**
   * Get available room types for modify booking workflow
   * This API is called when user is selecting a new room type
   */
  async getModifyBookingRoomTypes(
    hotelId: string,
    roomTypeCode?: string,
    arrivalDate?: string,
    departureDate?: string,
  ): Promise<any> {
    try {
      console.log('🏨 Getting modify booking room types:', {
        hotelId, roomTypeCode, arrivalDate, departureDate,
      });

      const requestBody: Record<string, any> = {};
      if (roomTypeCode) requestBody.roomCode = roomTypeCode;
      if (arrivalDate) requestBody.arrivalDate = arrivalDate;
      if (departureDate) requestBody.departureDate = departureDate;

      const response = await apiClient.post<ModifyBookingRoomTypesResponse>(
        `/tenants/${hotelId}/modify-booking/room-types`,
        requestBody
      );
      
      if (response.success && response.data) {
        console.log('✅ Modify booking room types retrieved:', response.data);
        const roomTypesResponse: ModifyBookingRoomTypesResponse = {
          success: response.success,
          message: (response as any).message || 'Room types retrieved successfully',
          data: response.data as any
        };
        return roomTypesResponse;
      } else {
        console.warn('⚠️ Failed to get modify booking room types:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to get room types';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to get modify booking room types:', error);
      throw error;
    }
  }

  /**
   * Get available room numbers for a specific room type
   * This API is called when user wants to see available room numbers for a room type
   */
  async getRoomNumbers(hotelId: string, request: GetRoomNumbersRequest): Promise<GetRoomNumbersResponse> {
    try {
      console.log('🏠 Getting room numbers for room type:', { hotelId, ...request });
      
      const response = await apiClient.post<GetRoomNumbersApiResponse>(
        `/tenants/${hotelId}/modify-booking/get-room-numbers`,
        {
          roomType: request.roomType
        }
      );
      
      if (response.success && response.data) {
        console.log('✅ Raw room numbers API response:', JSON.stringify(response.data, null, 2));
        
        // Handle different possible response structures
        let apiRooms: ApiRoomData[] = [];
        
        // Check if response.data is an object with nested structure
        if (typeof response.data === 'object' && !Array.isArray(response.data)) {
          const dataObj = response.data as any;
          
          if (dataObj.data?.hotelRoomsDetails?.room) {
            // Expected nested structure
            apiRooms = dataObj.data.hotelRoomsDetails.room;
          } else if (dataObj.hotelRoomsDetails?.room) {
            // Direct structure without nested 'data'
            apiRooms = dataObj.hotelRoomsDetails.room;
          } else if (Array.isArray(dataObj.room)) {
            // Room array directly in response.data
            apiRooms = dataObj.room;
          } else {
            console.warn('⚠️ Unexpected API response structure:', response.data);
            throw new Error('Unable to find room data in API response');
          }
        } else if (Array.isArray(response.data)) {
          // Response.data is the room array itself
          apiRooms = response.data as ApiRoomData[];
        } else {
          console.warn('⚠️ Unexpected API response structure:', response.data);
          throw new Error('Unable to find room data in API response');
        }
        
        // Additional check for response.hotelRoomsDetails (direct property)
        if (apiRooms.length === 0 && (response as any).hotelRoomsDetails?.room) {
          apiRooms = (response as any).hotelRoomsDetails.room;
        }
        
        console.log('📋 Found room data:', { roomCount: apiRooms.length, rooms: apiRooms });
        
        // Handle empty room data
        if (apiRooms.length === 0) {
          console.warn('⚠️ No rooms found for room type:', request.roomType);
          return {
            success: true,
            message: 'No rooms found for the specified room type',
            data: {
              roomNumbers: [],
              totalRooms: 0,
              availableRooms: 0,
              roomType: request.roomType
            }
          };
        }
        
        const roomNumbers: RoomNumber[] = apiRooms.map((apiRoom: ApiRoomData) => {
          // Remove negative sign from roomId and use as room number
          const roomNumber = apiRoom.roomId.replace(/^-/, '');
          
          return {
            roomNumber,
            roomType: apiRoom.roomType?.roomType || 'Unknown',
            roomTypeCode: apiRoom.roomType?.roomType || 'Unknown',
            isAvailable: true // All rooms are selectable
          };
        });
        
        // Calculate totals
        const totalRooms = roomNumbers.length;
        const availableRooms = roomNumbers.length; // All rooms are available for selection
        
        console.log('✅ Room numbers processed successfully:', {
          roomType: request.roomType,
          totalRooms,
          availableRooms,
          roomNumbers: roomNumbers.length,
          sampleRoom: roomNumbers[0]
        });
        
        const transformedResponse: GetRoomNumbersResponse = {
          success: true,
          message: 'Room numbers retrieved successfully',
          data: {
            roomNumbers,
            totalRooms,
            availableRooms,
            roomType: request.roomType
          }
        };
        
        return transformedResponse;
      } else {
        console.warn('⚠️ Failed to get room numbers:', response.error);
        const errorMessage = response.error?.message || 'Failed to get room numbers';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to get room numbers:', error);
      throw error;
    }
  }

  /**
   * Check if breakfast is included in a reservation
   * This API is called when user selects a reservation to check breakfast details
   */
  async checkBreakfastIncluded(hotelId: string, reservationId: string, conversationId: string, workflowSessionId: string, adultCount?: number, childrenCount?: number): Promise<BreakfastIncludedResponse> {
    try {
      console.log('🍳 Checking breakfast included for reservation:', { hotelId, reservationId, conversationId, workflowSessionId, adultCount, childrenCount });
      
      const requestBody: any = {
        conversationId,
        workflowSessionId
      };

      // Include guest counts if provided
      if (adultCount !== undefined) {
        requestBody.adultCount = adultCount;
      }
      if (childrenCount !== undefined) {
        requestBody.childrenCount = childrenCount;
      }

      const response = await apiClient.post(
        `/tenants/${hotelId}/reservations/${reservationId}/is-breakfast-included`,
        requestBody
      );
      console.log('🍳 Raw API response:', response);
      
      // The apiClient returns {success, data} format
      // Based on your API example, the actual data is in response.data
      if (response.success) {
        const responseData = response;
        console.log('🍳 Response data:', responseData);
        
        // Check if we have the expected breakfast analysis structure
        if (responseData && ((responseData as any).breakfastAnalysis || responseData.success !== undefined)) {
          console.log('✅ Breakfast check successful:', responseData);
          
          const breakfastResponse: BreakfastIncludedResponse = {
            success: true,
            message: 'Breakfast details retrieved successfully',
            breakfastAnalysis: (responseData as any).breakfastAnalysis,
            workflowSessionId: (responseData as any).workflowSessionId,
            conversationId: (responseData as any).conversationId,
            breakfastPricing: (responseData as any).breakfastPricing,
            ohipData: (responseData as any).ohipData
          };
          return breakfastResponse;
        } else {
          console.warn('⚠️ Expected breakfast data not found in response:', responseData);
          throw new Error('Invalid response format: missing breakfast analysis data');
        }
      } else {
        console.warn('⚠️ API request failed:', response.error || response);
        const errorMessage = response.error?.message || 'API request failed';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to check breakfast included:', error);
      
      // Log more details about the error
      if (error.response) {
        console.error('❌ Error response:', error.response.data);
        console.error('❌ Error status:', error.response.status);
      } else if (error.request) {
        console.error('❌ No response received:', error.request);
      } else {
        console.error('❌ Error message:', error.message);
      }
      
      throw error;
    }
  }

  /**
   * Get reservation details for modify booking workflow
   * This API is called when user selects a reservation to modify
   */
  async getModifyBookingReservation(hotelId: string, request: ModifyBookingReservationRequest): Promise<any> {
    try {
      console.log('📝 Getting modify booking reservation details:', { hotelId, ...request });
      
      const response = await apiClient.post<ModifyBookingResponse>(
        `/tenants/${hotelId}/modify-booking/reservation`,
        {
          workflowSessionId: request.workflowSessionId,
          conversationId: request.conversationId,
          email: request.email,
          agent: request.agentName
        }
      );
      
      if (response.success && response.data) {
        console.log('✅ Modify booking reservation details retrieved:', response.data);
        const reservationResponse: ModifyBookingResponse = {
          success: response.success,
          message: (response as any).message || 'Reservation details retrieved successfully',
          data: response.data as any
        };
        return reservationResponse;
      } else {
        console.warn('⚠️ Failed to get modify booking reservation:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to get reservation details';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to get modify booking reservation:', error);
      throw error;
    }
  }

  /**
   * Get payment details for selected room in modify booking workflow
   * This API reuses the existing booking payment endpoint
   */
  async getModifyBookingPayment(hotelId: string, request: ModifyBookingPaymentRequest): Promise<ModifyBookingPaymentResponse> {
    try {
      console.log('💳 Getting modify booking payment details using existing booking API:', { hotelId, ...request });
      
      const requestBody: any = {
        roomCode: request.roomCode,
        conversationId: request.conversationId,
        workflowSessionId: request.workflowSessionId
      };

      // Include arrival and departure dates if provided
      if (request.arrivalDate) {
        requestBody.arrivalDate = request.arrivalDate;
      }
      if (request.departureDate) {
        requestBody.departureDate = request.departureDate;
      }
      // Include guest count and room details if provided
      if (request.adults !== undefined) {
        requestBody.adults = request.adults;
      }
      if (request.children !== undefined) {
        requestBody.children = request.children;
      }
      if (request.numberOfRooms !== undefined) {
        requestBody.numberOfRooms = request.numberOfRooms;
      }
      // Include ratePlanCode parameter if provided
      if (request.ratePlanCode !== undefined) {
        requestBody.ratePlanCode = request.ratePlanCode;
      }
      // Include agent parameter if provided
      if (request.agent !== undefined) {
        requestBody.agent = request.agent;
      }
      // Include previousRoomCode parameter if provided
      if (request.previousRoomCode !== undefined) {
        requestBody.previousRoomCode = request.previousRoomCode;
      }
      // Include guestEmail parameter if provided
      if (request.guestEmail !== undefined) {
        requestBody.guestEmail = request.guestEmail;
      }
      // Include reservationId parameter if provided
      if (request.reservationId !== undefined) {
        requestBody.reservationId = request.reservationId;
      }

      const response = await apiClient.post<ModifyBookingPaymentResponse>(
        `/tenants/${hotelId}/booking/step/payment`,
        requestBody
      );
      
      if (response.success && response.data) {
        console.log('✅ Modify booking payment details retrieved from booking API:', response.data);
        const paymentResponse: ModifyBookingPaymentResponse = {
          success: response.success,
          data: response.data as any
        };
        return paymentResponse;
      } else {
        console.warn('⚠️ Failed to get modify booking payment details:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to get payment details';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to get modify booking payment details:', error);
      throw error;
    }
  }

  /**
   * Modify reservation with new room type and dates
   * This API is called in step 3 when user accepts the upgrade
   */
  async modifyReservation(hotelId: string, request: ModifyReservationRequest): Promise<ModifyReservationResponse> {
    try {
      console.log('🔄 Modifying reservation:', { hotelId, ...request });
      
      const requestBody: any = {
        reservationId: request.reservationId,
        selectedRoomCode: request.selectedRoomCode,
        arrivalDate: request.arrivalDate,
        departureDate: request.departureDate,
        numberOfRooms: request.numberOfRooms,
        workflowSessionId: request.workflowSessionId,
        conversationId: request.conversationId
      };

      // Include guest count if provided
      if (request.adults !== undefined) {
        requestBody.adults = request.adults;
      }
      if (request.children !== undefined) {
        requestBody.children = request.children;
      }
      // Include agent parameter if provided
      if (request.agent !== undefined) {
        requestBody.agent = request.agent;
      }
      // Include payment method token if provided
      if (request.paymentMethodToken !== undefined) {
        requestBody.paymentMethodToken = request.paymentMethodToken;
      }
      // Include payment type if provided
      if (request.paymentType !== undefined) {
        requestBody.paymentType = request.paymentType;
      }

      const response = await apiClient.put<ModifyReservationResponse>(
        `/tenants/${hotelId}/modify-booking/modify-reservation`,
        requestBody
      );
      
      if (response.success && response.data) {
        console.log('✅ Reservation modified successfully:', response.data);
        const modifyResponse: ModifyReservationResponse = {
          success: response.success,
          message: (response as any).message || 'Reservation modified successfully',
          data: response.data as any
        };
        return modifyResponse;
      } else {
        console.warn('⚠️ Failed to modify reservation:', response.error);
        throw new Error(response.error?.message || 'Failed to modify reservation');
      }
    } catch (error) {
      console.error('❌ Failed to modify reservation:', error);
      throw error;
    }
  }

  /**
   * Update checkout time for late checkout
   * PUT /tenants/:hotelId/modify-booking/update-checkout-time
   */
  async updateCheckoutTime(hotelId: string, request: UpdateCheckoutTimeRequest): Promise<UpdateCheckoutTimeResponse> {
    try {
      console.log('🕐 Updating checkout time:', { hotelId, ...request });

      const requestBody: any = {
        reservationId: request.reservationId,
        arrivalDate: request.arrivalDate,
        departureDate: request.departureDate,
        newCheckOutTime: request.newCheckOutTime,
        workflowSessionId: request.workflowSessionId,
        conversationId: request.conversationId,
        agent: request.agent
      };

      // Include guestEmail parameter if provided
      if (request.guestEmail !== undefined) {
        requestBody.guestEmail = request.guestEmail;
      }

      const response = await apiClient.put<any>(
        `/tenants/${hotelId}/modify-booking/update-checkout-time`,
        requestBody
      );

      if (response.success) {
        console.log('✅ Checkout time updated successfully:', response.data);
        return {
          success: response.success,
          message: 'Checkout time updated successfully',
          data: response.data
        } as UpdateCheckoutTimeResponse;
      } else {
        console.warn('⚠️ Failed to update checkout time:', response.error);
        throw new Error(response.error?.message || 'Failed to update checkout time');
      }
    } catch (error) {
      console.error('❌ Failed to update checkout time:', error);
      throw error;
    }
  }

  /**
   * Cancel a reservation
   * POST /tenants/:hotelId/booking/cancel-reservation
   */
  async cancelReservation(
    hotelId: string, 
    request: {
      reservationId: string;
      workflowSessionId: string;
      conversationId: string;
    }
  ): Promise<{ success: boolean; message?: string; data?: any }> {
    try {
      console.log('🚫 Cancelling reservation:', { hotelId, ...request });
      
      const response = await apiClient.post<{ success: boolean; message?: string; data?: any }>(
        `/tenants/${hotelId}/booking/cancel-reservation`,
        {
          reservationId: request.reservationId,
          workflowSessionId: request.workflowSessionId,
          conversationId: request.conversationId
        }
      );
      
      if (response.success && response.data) {
        console.log('✅ Reservation cancellation successful:', response.data);
        const cancelResponse = {
          success: response.success,
          message: (response as any).message || response.data?.message || 'Reservation cancelled successfully',
          data: response.data as any
        };
        return cancelResponse;
      } else {
        console.warn('⚠️ Reservation cancellation failed:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to cancel reservation';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to cancel reservation:', error);
      throw error;
    }
  }

  /**
   * Add breakfast packages to a reservation
   * POST /tenants/:hotelId/reservations/:reservationId/add-breakfast-packages
   */
  async addBreakfastPackages(
    hotelId: string,
    reservationId: string,
    request: AddBreakfastPackageRequest
  ): Promise<AddBreakfastPackageResponse> {
    try {
      console.log('🍳 Adding breakfast packages:', { hotelId, reservationId, ...request });
      
      const response = await apiClient.post<AddBreakfastPackageResponse>(
        `/tenants/${hotelId}/reservations/${reservationId}/add-breakfast-packages`,
        {
          conversationId: request.conversationId,
          workflowSessionId: request.workflowSessionId,
          breakfastCodes: request.breakfastCodes,
          adultCount: request.adultCount,
          childrenCount: request.childrenCount
        }
      );
      
      if (response.success) {
        console.log('✅ Breakfast packages added successfully:', response);
        
        const addBreakfastResponse: AddBreakfastPackageResponse = {
          success: response.success,
          message: (response as any).message || 'Breakfast packages added successfully',
          data: (response as any).data,
          workflowSessionId: (response as any).workflowSessionId,
          conversationId: (response as any).conversationId
        };
        return addBreakfastResponse;
      } else {
        console.warn('⚠️ Failed to add breakfast packages:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to add breakfast packages';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to add breakfast packages:', error);
      throw error;
    }
  }

  /**
   * Update check-in time for a reservation
   */
  async updateCheckInTime(hotelId: string, request: UpdateCheckInTimeRequest): Promise<UpdateCheckInTimeResponse> {
    try {
      console.log('📝 Updating check-in time:', request);

      const response = await apiClient.put(
        `/tenants/${hotelId}/modify-booking/update-checkin-time`,
        request
      );

      if (response.success && response.data) {
        console.log('✅ Check-in time updated successfully:', response.data);
        return response.data;
      } else {
        console.warn('⚠️ Failed to update check-in time:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to update check-in time';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to update check-in time:', error);
      throw error;
    }
  }

  /**
   * Send reservation summary email (receipt)
   */
  async sendReservationSummaryEmail(hotelId: string, request: {
    reservationId: string;
    workflowSessionId: string;
    conversationId: string;
  }): Promise<{ success: boolean; message?: string }> {
    try {
      console.log('📝 Sending reservation summary email:', request);

      const response = await apiClient.post(
        `/tenants/${hotelId}/send-reservation-summary-email`,
        request
      );

      if (response.success) {
        console.log('✅ Reservation summary email sent successfully:', response.data);
        return { success: true, message: response.data?.message };
      } else {
        console.warn('⚠️ Failed to send reservation summary email:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to send receipt email';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Error sending reservation summary email:', error);
      throw error;
    }
  }

  /**
   * Get available upgrades for check-in process
   * This API is called in step 3 of check-in modal to get available upgrades
   */
  async getAvailableUpgrades(hotelId: string, request: UpgradeUpsellRequest): Promise<UpgradeUpsellResponse> {
    try {
      console.log('🔄 Getting available upgrades for check-in:', { hotelId, ...request });
      
      const response = await apiClient.post<UpgradeUpsellResponse>(
        `/tenants/${hotelId}/show-available-upgrades`,
        {
          numberOfRooms: request.numberOfRooms,
          roomType: request.roomType,
          startDate: request.startDate,
          endDate: request.endDate,
          adults: request.adults,
          children: request.children,
          reservationId: request.reservationId,
          conversationId: request.conversationId,
          workflowSessionId: request.workflowSessionId
        }
      );
      
      if (response.success && response.data) {
        console.log('✅ Available upgrades retrieved successfully:', response.data);
        return response.data;
      } else {
        console.warn('⚠️ Failed to get available upgrades:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to get available upgrades';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to get available upgrades:', error);
      throw error;
    }
  }

  /**
   * Upgrade room reservation
   * This API is called in step 5 of check-in modal when processing payment for upgraded room
   */
  async upgradeRoom(hotelId: string, request: UpgradeRoomRequest): Promise<UpgradeRoomResponse> {
    try {
      console.log('🔄 Upgrading room reservation:', { hotelId, ...request });
      
      const response = await apiClient.post<UpgradeRoomResponse>(
        `/tenants/${hotelId}/upgrade-room`,
        {
          reservationId: request.reservationId,
          adults: request.adults,
          children: request.children,
          startDate: request.startDate,
          endDate: request.endDate,
          previousRoomCode: request.previousRoomCode,
          roomCode: request.roomCode,
          ...(request.conversationId && { conversationId: request.conversationId }),
          ...(request.workflowSessionId && { workflowSessionId: request.workflowSessionId })
        }
      );
      
      if (response.success && response.data) {
        console.log('✅ Room upgrade successful:', response.data);
        const upgradeResponse: UpgradeRoomResponse = {
          success: response.success,
          message: (response as any).message || 'Room upgraded successfully',
          data: response.data as any
        };
        return upgradeResponse;
      } else {
        console.warn('⚠️ Failed to upgrade room:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to upgrade room';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to upgrade room:', error);
      throw error;
    }
  }

  /**
   * Get available room numbers for a specific room type and date range
   * This API is called during express check-in to allow guest to select their preferred room
   */
  async getAvailableRoomNumbers(
    hotelId: string,
    request: GetAvailableRoomNumbersRequest
  ): Promise<GetAvailableRoomNumbersResponse> {
    try {
      console.log('🔄 Fetching available room numbers:', { hotelId, ...request });

      const response = await apiClient.post<GetAvailableRoomNumbersResponse>(
        `/tenants/${hotelId}/reservation/get-room-numbers`,
        {
          roomTypeCode: request.roomTypeCode,
          startDate: request.startDate,
          endDate: request.endDate
        }
      );

      if (response.success && response.data) {
        console.log('✅ Available rooms fetched successfully:', response.data);
        const roomNumbersResponse: GetAvailableRoomNumbersResponse = {
          success: response.success,
          message: (response as any).message || 'Available rooms fetched successfully',
          data: response.data as any
        };
        return roomNumbersResponse;
      } else {
        console.warn('⚠️ Failed to fetch available rooms:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to fetch available rooms';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to fetch available room numbers:', error);
      throw error;
    }
  }

  /**
   * Complete check-in process
   * This API is called in step 5 of check-in modal after payment processing (and after upgrade if applicable)
   */
  async checkIn(hotelId: string, request: CheckInRequest): Promise<CheckInResponse> {
    try {
      console.log('🔄 BookingService - Completing check-in process:', { hotelId, ...request });
      
      const requestBody = {
        reservationId: request.reservationId,
        ...(request.conversationId && { conversationId: request.conversationId }),
        ...(request.workflowSessionId && { workflowSessionId: request.workflowSessionId }),
        ...(request.paymentMethodToken && { paymentMethodToken: request.paymentMethodToken })
      };
      
      console.log('📤 BookingService - Check-in API request body:', {
        requestBody,
        hasPaymentMethodToken: !!request.paymentMethodToken,
        paymentMethodTokenValue: request.paymentMethodToken,
        requestBodyKeys: Object.keys(requestBody)
      });
      
      const response = await apiClient.post<CheckInResponse>(
        `/tenants/${hotelId}/checkin`,
        requestBody
      );
      
      if (response.success && response.data) {
        console.log('✅ Check-in completed successfully:', response.data);
        const checkInResponse: CheckInResponse = {
          success: response.success,
          message: (response as any).message || 'Check-in completed successfully',
          data: response.data as any
        };
        return checkInResponse;
      } else {
        console.warn('⚠️ Failed to complete check-in:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to complete check-in';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to complete check-in:', error);
      throw error;
    }
  }

  /**
   * Assign a specific room to a reservation
   */
  async assignSpecificRoom(hotelId: string, request: AssignSpecificRoomRequest): Promise<AssignSpecificRoomResponse> {
    try {
      console.log('🏨 Assigning specific room:', request);
      
      const response = await apiClient.post<ApiResponse<AssignSpecificRoomResponse>>(
        `/tenants/${hotelId}/assign-specific-room`,
        {
          roomNumber: request.roomNumber,
          roomCode: request.roomCode,
          reservationId: request.reservationId,
          startDate: request.startDate,
          endDate: request.endDate,
          ...(request.conversationId && { conversationId: request.conversationId }),
          ...(request.workflowSessionId && { workflowSessionId: request.workflowSessionId })
        }
      );
      
      if (response.success && response.data) {
        console.log('✅ Room assigned successfully:', response.data);
        return response.data as unknown as AssignSpecificRoomResponse;
      } else {
        console.warn('⚠️ Failed to assign specific room:', response.error);
        const errorMessage = response.error?.message || 'Failed to assign specific room';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to assign specific room:', error);
      throw error;
    }
  }

  /**
   * Get workflow inventory items (e.g., rollaway bed pricing)
   */
  async getWorkflowInventory(hotelId: string, request: WorkflowInventoryRequest): Promise<WorkflowInventoryResponse> {
    console.log('🛏️ Getting workflow inventory:', { hotelId, request });
    
    try {
      const response = await apiClient.post<WorkflowInventoryResponse>(
        `/tenants/${hotelId}/workflow-inventory`,
        request
      );
      
      if (response.success && response.data) {
        console.log('✅ Workflow inventory retrieved successfully:', response.data);
        return response.data;
      } else {
        console.warn('⚠️ Failed to get workflow inventory:', response.error);
        const errorMessage = response.error?.message || 'Failed to get workflow inventory';
        throw new Error(errorMessage);
      }
    } catch (error) {
      console.error('❌ Failed to get workflow inventory:', error);
      throw error;
    }
  }


  /**
   * Generate digital room keys for a reservation
   */
  async generateRoomKeys(hotelId: string, request: GenerateRoomKeysRequest): Promise<GenerateRoomKeysResponse> {
    console.log('🗝️ Generating digital room keys:', { hotelId, request });
    
    try {
      const response = await apiClient.post<GenerateRoomKeysResponse>(
        `/tenants/${hotelId}/generate-room-keys`,
        {
          reservationId: request.reservationId
        }
      );
      
      // Check if the API call was successful
      if (response.success && response.data) {
        // Check the status field in the response data
        if (response.data.status === 'true' || response.data.status === 'SUCCESS') {
          console.log('✅ Digital room keys generated successfully:', response.data);
          return response.data;
        } else {
          // API call succeeded but key generation failed
          console.warn('⚠️ Room key generation failed:', response.data);
          const errorMessage = response.data.message || 'Failed to generate digital room keys';
          throw new Error(errorMessage);
        }
      } else {
        // Handle different error scenarios based on HTTP status and message
        console.warn('⚠️ API call failed:', response.error);
        
        let errorMessage = 'Failed to generate digital room keys';
        
        if (response.error?.message) {
          errorMessage = response.error.message;
        } else if ((response as any).message) {
          errorMessage = (response as any).message;
        }
        
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to generate digital room keys:', error);
      
      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Add comments to a reservation for special dietary requirements or other modifications
   */
  async addCommentToReservation(hotelId: string, request: AddCommentToReservationRequest): Promise<AddCommentToReservationResponse> {
    try {
      console.log('📝 Adding comments to reservation:', request);
      
      const response = await apiClient.post<AddCommentToReservationResponse>(
        `/tenants/${hotelId}/modify-booking/add-comment-to-reservation`,
        request
      );

      if (response.success) {
        console.log('✅ Comments added successfully:', response);
        return response;
      } else {
        console.warn('⚠️ Failed to add comments:', response);
        const errorMessage = response.error?.message || 'Failed to add comments to reservation';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to add comments to reservation:', error);
      
      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Submit housekeeping service request
   */
  async submitHousekeepingRequest(hotelId: string, request: HousekeepingServiceRequest): Promise<HousekeepingServiceResponse> {
    try {
      console.log('🧹 Submitting housekeeping service request:', { hotelId, ...request });
      
      const response = await apiClient.post<HousekeepingServiceResponse>(
        `/tenants/${hotelId}/service-request/housekeeping`,
        {
          reservationId: request.reservationId,
          description: request.description,
          ...(request.workflowSessionId && { workflowSessionId: request.workflowSessionId }),
          ...(request.conversationId && { conversationId: request.conversationId }),
          ...(request.agentName && { agentName: request.agentName })
        }
      );

      if (response.success) {
        console.log('✅ Housekeeping request submitted successfully:', response);
        return response as HousekeepingServiceResponse;
      } else {
        console.warn('⚠️ Failed to submit housekeeping request:', response.error);
        const errorMessage = response.error?.message || 'Failed to submit housekeeping request';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to submit housekeeping request:', error);
      
      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Submit a front desk service request
   */
  async submitFrontDeskRequest(hotelId: string, request: FrontDeskServiceRequest): Promise<FrontDeskServiceResponse> {
    try {
      console.log('🏨 Submitting front desk service request:', { hotelId, ...request });
      
      const response = await apiClient.post<FrontDeskServiceResponse>(
        `/tenants/${hotelId}/service-request/frontdesk`,
        {
          reservationId: request.reservationId,
          description: request.description,
          priority: request.priority || "STANDARD",
          workflowSessionId: request.workflowSessionId,
          conversationId: request.conversationId,
          agentName: request.agentName,
          troubleshooting: request.troubleshooting
        }
      );

      if (response.success) {
        console.log('✅ Front desk request submitted successfully:', response);
        return response as FrontDeskServiceResponse;
      } else {
        console.warn('⚠️ Failed to submit front desk request:', response.error);
        const errorMessage = response.error?.message || 'Failed to submit front desk request';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to submit front desk request:', error);
      
      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }
      
      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Submit a birthday celebration request
   */
  async submitBirthdayCelebrationRequest(hotelId: string, request: BirthdayCelebrationRequest): Promise<BirthdayCelebrationResponse> {
    try {
      console.log('🎉 Submitting birthday celebration request:', { hotelId, ...request });

      const response = await apiClient.post<BirthdayCelebrationResponse>(
        `/tenants/${hotelId}/birthday-celebration`,
        {
          reservationId: request.reservationId,
          celebrationData: request.celebrationData,
          workflowSessionId: request.workflowSessionId,
          conversationId: request.conversationId,
          agentName: request.agentName
        }
      );

      if (response.success) {
        console.log('✅ Birthday celebration request submitted successfully:', response);
        return response as BirthdayCelebrationResponse;
      } else {
        console.warn('⚠️ Failed to submit birthday celebration request:', response.error);
        const errorMessage = response.error?.message || 'Failed to submit birthday celebration request';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to submit birthday celebration request:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Add billing to reservation for late checkout
   */
  async addBillingToReservation(hotelId: string, request: AddBillingToReservationRequest): Promise<AddBillingToReservationResponse> {
    try {
      console.log('💰 Adding billing to reservation:', { hotelId, ...request });

      const requestBody: any = {
        reservationId: request.reservationId,
        agentName: request.agentName,
        workflowSessionId: request.workflowSessionId,
        conversationId: request.conversationId
      };

      // Include newCheckOutTime if provided
      if (request.newCheckOutTime) {
        requestBody.newCheckOutTime = request.newCheckOutTime;
      }

      const response = await apiClient.post<AddBillingToReservationResponse>(
        `/tenants/${hotelId}/add-billing-to-reservation`,
        requestBody
      );

      if (response.success) {
        console.log('✅ Billing added to reservation successfully:', response);
        return response as AddBillingToReservationResponse;
      } else {
        console.warn('⚠️ Failed to add billing to reservation:', response.error);
        const errorMessage = response.error?.message || 'Failed to add billing to reservation';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to add billing to reservation:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  async reconfirmReservation(hotelId: string, request: ReconfirmReservationRequest): Promise<ReconfirmReservationResponse> {
    try {
      const requestBody: any = {
        guestEmail: request.guestEmail,
        reservationId: request.reservationId
      };

      // Include optional phone update fields
      if (request.phoneNumber) {
        requestBody.phoneNumber = request.phoneNumber;
        requestBody.phoneType = request.phoneType || 'HOME';
        requestBody.isPrimary = request.isPrimary ?? true;
        if (request.phoneId) {
          requestBody.phoneId = request.phoneId;
        }
      }

      // Include optional check-in time update
      if (request.arrivalDate) requestBody.arrivalDate = request.arrivalDate;
      if (request.departureDate) requestBody.departureDate = request.departureDate;
      if (request.newCheckInTime) requestBody.newCheckInTime = request.newCheckInTime;

      // Include optional comments
      if (request.comments) requestBody.comments = request.comments;

      // Include workflow tracking
      if (request.workflowSessionId) requestBody.workflowSessionId = request.workflowSessionId;
      if (request.conversationId) requestBody.conversationId = request.conversationId;

      const response = await apiClient.post<ReconfirmReservationResponse>(
        `/reservations/${hotelId}/reconfirm-reservation`,
        requestBody
      );

      if (response.success) {
        return response as ReconfirmReservationResponse;
      } else {
        const errorMessage = response.error?.message || 'Failed to reconfirm reservation';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('Failed to reconfirm reservation:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Assign a room to a reservation
   * POST /tenants/:hotelId/reservation/assign-room
   */
  async assignRoom(hotelId: string, request: AssignRoomRequest): Promise<AssignRoomResponse> {
    try {
      console.log('🏨 Assigning room to reservation:', { hotelId, ...request });

      const response = await apiClient.post(
        `/tenants/${hotelId}/reservation/assign-room`,
        request
      );

      console.log('🏨 Assign room API full response:', response);
      console.log('🏨 Assign room API response data:', response.data);

      // apiClient returns unwrapped data: { success: true, data: {...} }
      if (response && response.success) {
        console.log('✅ Room assigned successfully:', response.data);
        return response as AssignRoomResponse;
      } else {
        const errorMessage = (response as any)?.data?.message || (response as any)?.message || 'Failed to assign room';
        console.error('❌ API returned success=false:', errorMessage);
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Caught error in assignRoom:', error);
      console.error('❌ Error response:', error.response);
      console.error('❌ Error response data:', error.response?.data);
      console.error('❌ Error response status:', error.response?.status);

      // CRITICAL: Check if the API actually succeeded despite being caught as an error
      // This happens when the backend returns a non-2xx status code but with success: true in the body
      if (error.response?.data?.success === true) {
        console.log('⚠️ API returned success=true but with error HTTP status code');
        console.log('⚠️ Treating as successful response and returning data');
        return error.response.data;
      }

      // Handle HTTP response errors - only if success !== true
      if (error.response?.data?.data?.message) {
        throw new Error(error.response.data.data.message);
      } else if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Remove packages from a reservation
   * POST /tenants/:hotelId/remove-package-from-reservation
   */
  async removePackageFromReservation(
    hotelId: string,
    request: RemovePackageFromReservationRequest
  ): Promise<RemovePackageFromReservationResponse> {
    try {
      console.log('🗑️ Removing packages from reservation:', { hotelId, ...request });

      const requestBody: any = {
        reservationId: request.reservationId,
        packageCodes: request.packageCodes
      };

      // Include workflow tracking
      if (request.workflowSessionId) requestBody.workflowSessionId = request.workflowSessionId;
      if (request.conversationId) requestBody.conversationId = request.conversationId;

      const response = await apiClient.post<RemovePackageFromReservationResponse>(
        `/tenants/${hotelId}/remove-package-from-reservation`,
        requestBody
      );

      if (response.success) {
        console.log('✅ Packages removed successfully:', response);
        return response as RemovePackageFromReservationResponse;
      } else {
        console.warn('⚠️ Failed to remove packages:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to remove packages from reservation';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to remove packages from reservation:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Add inventory item to a reservation
   * POST /tenants/:hotelId/add-inventory-to-reservation
   */
  async addInventoryToReservation(
    hotelId: string,
    request: AddInventoryToReservationRequest
  ): Promise<AddInventoryToReservationResponse> {
    try {
      console.log('🎁 Adding inventory item to reservation:', { hotelId, ...request });

      const requestBody = {
        reservationId: request.reservationId,
        agentName: request.agentName,
        inventoryCode: request.inventoryCode,
        workflowSessionId: request.workflowSessionId,
        conversationId: request.conversationId,
        ...(request.inventoryComment && { inventoryComment: request.inventoryComment })
      };

      const response = await apiClient.post<AddInventoryToReservationResponse>(
        `/tenants/${hotelId}/add-inventory-to-reservation`,
        requestBody
      );

      if (response.success) {
        console.log('✅ Inventory item added successfully:', response);
        return response as AddInventoryToReservationResponse;
      } else {
        console.warn('⚠️ Failed to add inventory item:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to add inventory item to reservation';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to add inventory item to reservation:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Add package to a reservation
   * POST /tenants/:hotelId/add-package-to-reservation
   */
  async addPackageToReservation(
    hotelId: string,
    request: AddPackageToReservationRequest
  ): Promise<AddPackageToReservationResponse> {
    try {
      console.log('📦 Adding package to reservation:', { hotelId, ...request });

      const requestBody = {
        reservationId: request.reservationId,
        agent: request.agent,
        packageCode: request.packageCode,
        workflowSessionId: request.workflowSessionId,
        conversationId: request.conversationId,
        ...(request.packageComment && { packageComment: request.packageComment })
      };

      const response = await apiClient.post<AddPackageToReservationResponse>(
        `/tenants/${hotelId}/add-package-to-reservation`,
        requestBody
      );

      if (response.success) {
        console.log('✅ Package added successfully:', response);
        return response as AddPackageToReservationResponse;
      } else {
        console.warn('⚠️ Failed to add package:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to add package to reservation';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to add package to reservation:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Add comments/requests to reservation
   * @param hotelId - Hotel identifier
   * @param request - Request data
   * @returns Promise<AddCommentsToReservationResponse>
   */
  async addCommentsToReservation(
    hotelId: string,
    request: AddCommentsToReservationRequest
  ): Promise<AddCommentsToReservationResponse> {
    try {
      console.log('📝 Adding comment/request to reservation:', { hotelId, ...request });

      const requestBody = {
        reservationId: request.reservationId,
        agent: request.agent,
        code: request.code,
        comment: request.comment,
        workflowSessionId: request.workflowSessionId,
        conversationId: request.conversationId
      };

      const response = await apiClient.post<AddCommentsToReservationResponse>(
        `/tenants/${hotelId}/add-comments-to-reservation`,
        requestBody
      );

      if (response.success) {
        console.log('✅ Comment/request added successfully:', response);
        return response as AddCommentsToReservationResponse;
      } else {
        console.warn('⚠️ Failed to add comment/request:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to add comment/request to reservation';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to add comment/request to reservation:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Get hotel packages
   * GET /tenants/:hotelId/get-hotel-packages
   */
  async getHotelPackages(
    hotelId: string,
    workflowSessionId?: string,
    conversationId?: string
  ): Promise<HotelPackagesResponse> {
    try {
      console.log('📦 Fetching hotel packages:', { hotelId, workflowSessionId, conversationId });

      const params: any = {};
      if (workflowSessionId) params.workflowSessionId = workflowSessionId;
      if (conversationId) params.conversationId = conversationId;

      const response = await apiClient.get<HotelPackagesResponse>(
        `/tenants/${hotelId}/get-hotel-packages`,
        { params }
      );

      if (response.success) {
        console.log('✅ Hotel packages fetched successfully:', response);
        return response as HotelPackagesResponse;
      } else {
        console.warn('⚠️ Failed to fetch hotel packages:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to fetch hotel packages';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to fetch hotel packages:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Get balance payment details for checkout
   * POST /reservations/:hotelId/get-balance-payment
   */
  async getBalancePayment(
    hotelId: string,
    request: GetBalancePaymentRequest
  ): Promise<GetBalancePaymentResponse> {
    try {
      console.log('💰 Fetching balance payment details:', { hotelId, ...request });

      const response = await apiClient.post<GetBalancePaymentResponse>(
        `/reservations/${hotelId}/get-balance-payment`,
        {
          reservationId: request.reservationId
        }
      );

      if (response.success && response.data) {
        console.log('✅ Balance payment details fetched successfully:', response.data);
        return response as GetBalancePaymentResponse;
      } else {
        console.warn('⚠️ Failed to fetch balance payment details:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to fetch balance payment details';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to fetch balance payment details:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Complete checkout for a reservation
   * POST /api/tenants/:hotelId/checkout
   */
  async completeCheckout(
    hotelId: string,
    request: {
      reservationId: string;
      conversationId: string;
      workflowSessionId: string;
      paymentMethodToken?: string;
    }
  ): Promise<{ success: boolean; message?: string; data?: any }> {
    try {
      console.log('🏨 Completing checkout:', { hotelId, ...request });

      const response = await apiClient.post<{ success: boolean; message?: string; data?: any }>(
        `/tenants/${hotelId}/checkout`,
        {
          reservationId: request.reservationId,
          conversationId: request.conversationId,
          workflowSessionId: request.workflowSessionId,
          paymentMethodToken: request.paymentMethodToken
        }
      );

      if (response.success) {
        console.log('✅ Checkout completed successfully:', response.data);
        return response;
      } else {
        console.warn('⚠️ Failed to complete checkout:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to complete checkout';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to complete checkout:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Trigger a workflow agent by calling get-json-from-agent API
   * POST /tenants/:hotelId/get-json-from-agent
   */
  async triggerWorkflowAgent(
    hotelId: string,
    agent: string,
    workflowSessionId?: string,
    conversationId?: string,
    reservationId?: string
  ): Promise<TriggerWorkflowAgentResponse> {
    try {
      console.log('🚀 Triggering workflow agent:', { hotelId, agent, workflowSessionId, conversationId, reservationId });

      const payload: any = {
        agent
      };

      // Add workflowSessionId only if provided
      if (workflowSessionId) {
        payload.workflowSessionId = workflowSessionId;
      }

      // Add conversationId only if provided
      if (conversationId) {
        payload.conversationId = conversationId;
      }

      // Add reservationId only if provided (for arrival/departure workflows)
      if (reservationId) {
        payload.reservationId = reservationId;
      }

      const response = await apiClient.post<TriggerWorkflowAgentResponse>(
        `/tenants/${hotelId}/get-json-from-agent`,
        payload
      );

      if (response.success) {
        console.log('✅ Workflow agent triggered successfully:', response);
        return response;
      } else {
        console.warn('⚠️ Failed to trigger workflow agent:', response.error);
        const errorMessage = response.error?.message || (response as any).message || 'Failed to trigger workflow agent';
        throw new Error(errorMessage);
      }
    } catch (error: any) {
      console.error('❌ Failed to trigger workflow agent:', error);

      // Handle HTTP response errors
      if (error.response?.data?.message) {
        throw new Error(error.response.data.message);
      }

      // Handle network errors or other issues
      throw error;
    }
  }

  /**
   * Check OTA reservation details
   * Verifies if a reservation exists and can be claimed
   */
  async checkOtaReservation(
    hotelId: string,
    request: CheckOtaReservationRequest
  ): Promise<CheckOtaReservationResponse> {
    try {
      console.log('🔍 Checking OTA reservation:', request);

      const response = await apiClient.post<CheckOtaReservationResponse>(
        `/tenants/${hotelId}/reservation/check-ota-reservation`,
        request
      );

      if (response.data) {
        console.log('✅ OTA reservation check response:', response.data);
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to check OTA reservation');
      }
    } catch (error: any) {
      console.error('❌ Error checking OTA reservation:', error);

      // Handle API errors with specific error messages
      if (error.response?.data) {
        return error.response.data;
      }

      // Handle network errors or other issues
      return {
        success: false,
        message: 'Failed to connect to reservation system',
        canClaimOTABooking: false
      };
    }
  }

  /**
   * Claim an OTA booking
   * Associates an OTA reservation with the guest's account
   */
  async claimOtaBooking(
    hotelId: string,
    request: ClaimOtaBookingRequest
  ): Promise<ClaimOtaBookingResponse> {
    try {
      console.log('📝 Claiming OTA booking:', request);

      const response = await apiClient.post<ClaimOtaBookingResponse>(
        `/tenants/${hotelId}/reservation/claim-ota-booking`,
        request
      );

      if (response.data) {
        console.log('✅ OTA booking claimed successfully:', response.data);
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to claim OTA booking');
      }
    } catch (error: any) {
      console.error('❌ Error claiming OTA booking:', error);

      // Handle API errors with specific error messages
      if (error.response?.data) {
        return error.response.data;
      }

      // Handle network errors or other issues
      return {
        success: false,
        message: 'Internal server error'
      };
    }
  }

}

export interface TriggerWorkflowAgentResponse {
  success: boolean;
  message?: string;
  data?: any;
  error?: {
    message: string;
    code?: string;
  };
}

export interface WorkflowInventoryRequest {
  workflowSessionId: string;
  reservationId: string;
  agent: string;
}

export interface WorkflowInventoryItem {
  hotelId: string;
  itemId: {
    id: string;
    type: string;
  };
  itemName: {
    defaultText: string;
    translatedTexts: any[];
  };
  itemCode: string;
  sellInfo: {
    availableQty: number;
    qtyInStock: number;
    price: {
      amount: number;
    };
    webBookable: boolean;
  };
  description?: string;
  available?: boolean;
  quantity?: number;
  price?: number;
}

export interface WorkflowInventoryResponse {
  success: boolean;
  message?: string;
  items: WorkflowInventoryItem[];
  links: any[];
  // Legacy field for backward compatibility
  inventoryItems?: WorkflowInventoryItem[];
}

export interface AssignSpecificRoomRequest {
  roomNumber: string;
  roomCode: string;
  reservationId: string;
  startDate: string; // YYYY-MM-DD format
  endDate: string;   // YYYY-MM-DD format
  conversationId?: string;
  workflowSessionId?: string;
}

export interface AssignSpecificRoomResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId: string;
    roomNumber: string;
    roomCode: string;
    assignedAt: string;
  };
}

export interface GenerateRoomKeysRequest {
  reservationId: string;
}

export interface KeyDetail {
  keyId: string;
  keyType: string;
  roomNumber: string;
  validFrom: string;
  validTo: string;
  encoderTerminal: string;
}

export interface GenerateRoomKeysResponse {
  status: string;
  message: string;
  result?: {
    // OHIP API response data - structure depends on OHIP's actual response
    // This could include key details, validity periods, etc.
    [key: string]: any;
  };
}

export interface AddCommentToReservationRequest {
  reservationId: string;
  comments: {
    [key: string]: string;
  };
  workflowSessionId: string;
  conversationId: string;
  agent: string;
}

export interface AddCommentToReservationResponse {
  success: boolean;
  message?: string;
  data?: any;
}

export interface HousekeepingServiceRequest {
  reservationId: string;
  description: string;
  workflowSessionId?: string;
  conversationId?: string;
  agentName?: string;
}

export interface HousekeepingServiceResponse {
  success: boolean;
  message?: string;
  data?: {
    requestId?: string;
    reservationId?: string;
    status?: string;
    estimatedCompletionTime?: string;
  };
}

export interface FrontDeskServiceRequest {
  reservationId: string;
  description: string;
  priority?: string;
  workflowSessionId?: string;
  conversationId?: string;
  agentName?: string;
  troubleshooting?: boolean;
}

export interface FrontDeskServiceResponse {
  success: boolean;
  message?: string;
  data?: {
    requestId?: string;
    reservationId?: string;
    status?: string;
    priority?: string;
    estimatedResponseTime?: string;
  };
}

export interface BirthdayCelebrationRequest {
  reservationId: string;
  celebrationData: {
    birthdayPersonName: string;
    birthdayAge: number;
    birthdayDate: string;
    relationshipToBirthdayPerson: string;
    celebrationGuestCount: number;
    celebrationStyle: string;
    setupTiming: string;
    cakeFlavor: string;
    decorationTheme?: string;
    colorPreferences?: string;
    dietaryRestrictions?: string;
    specialRequests?: string;
    birthdayTraditions?: string;
  };
  workflowSessionId: string;
  conversationId: string;
  agentName?: string;
}

export interface BirthdayCelebrationResponse {
  success: boolean;
  message?: string;
  data?: {
    celebrationId?: string;
    reservationId?: string;
    status?: string;
    setupDate?: string;
    estimatedSetupTime?: string;
  };
}

export interface AddBillingToReservationRequest {
  reservationId: string;
  agentName: string;
  newCheckOutTime?: string; // HH:MM:SS format, optional
  workflowSessionId: string;
  conversationId: string;
}

export interface AddBillingToReservationResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId?: string;
    billingAdded?: boolean;
    newCheckOutTime?: string;
  };
}

export interface ReconfirmReservationRequest {
  guestEmail: string;
  reservationId: string;
  phoneNumber?: string;
  phoneType?: "HOME" | "BUSINESS";
  isPrimary?: boolean;
  phoneId?: string;
  arrivalDate?: string;
  departureDate?: string;
  newCheckInTime?: string;
  comments?: Record<string, string>;
  workflowSessionId?: string;
  conversationId?: string;
}

export interface ReconfirmReservationResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId?: string;
    confirmationId?: string;
    reconfirmedAt?: string;
  };
}

export interface RemovePackageFromReservationRequest {
  reservationId: string;
  packageCodes: string[];
  workflowSessionId?: string;
  conversationId?: string;
}

export interface RemovePackageFromReservationResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId?: string;
    packagesRemoved?: number;
    removedPackages?: string[];
  };
}

export interface AddInventoryToReservationRequest {
  reservationId: string;
  agentName: string;
  inventoryCode: string;
  workflowSessionId: string;
  conversationId: string;
  inventoryComment?: string;
}

export interface AddInventoryToReservationResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId?: string;
    inventoryItemId?: string;
    inventoryCode?: string;
  };
}

export interface AddPackageToReservationRequest {
  reservationId: string;
  agent: string;
  packageCode: string;
  workflowSessionId: string;
  conversationId: string;
  packageComment?: string;
}

export interface AddPackageToReservationResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId?: string;
    packageId?: string;
    packageCode?: string;
  };
}

export interface AddCommentsToReservationRequest {
  reservationId: string;
  agent: string;
  code: string;
  comment: string;
  workflowSessionId: string;
  conversationId: string;
}

export interface AddCommentsToReservationResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationId?: string;
    commentId?: string;
    code?: string;
  };
}

export interface HotelPackage {
  packageCode: string;
  packageName: string;
  description?: string;
  price?: number;
  currency?: string;
  available?: boolean;
}

export interface HotelPackagesResponse {
  success: boolean;
  message?: string;
  packages?: HotelPackage[];
  data?: {
    packages?: HotelPackage[];
  };
}

export interface GetBalancePaymentRequest {
  reservationId: string;
}

export interface FolioLineItem {
  date?: string;
  description: string;
  amount: number;
  quantity?: number;
  unitPrice?: number;
}

export interface GetBalancePaymentResponse {
  success: boolean;
  message?: string;
  data?: {
    reservationFolioInformation?: {
      reservationInfo?: {
        reservationIdList?: Array<{ id: string; type: string }>;
        roomStay?: {
          arrivalDate: string;
          departureDate: string;
          adultCount: number;
          childCount: number;
          numberOfRooms: number;
          rateAmount: {
            amount: number;
            currencyCode: string;
          };
          balance: {
            amount: number;
            currencyCode: string;
          };
          depositPayments?: {
            amount: number;
            currencyCode: string;
          };
        };
      };
      folioWindows?: Array<{
        revenue: { amount: number; currencyCode: string };
        payment: { amount: number; currencyCode: string };
        balance: { amount: number; currencyCode: string };
        folios?: Array<{
          postings?: Array<{
            reference: string;
            postedAmount: { amount: number; currencyCode: string };
            transactionType: string;
          }>;
        }>;
      }>;
    };
    reservationId?: string;
    items?: FolioLineItem[];
    subtotal?: number;
    taxes?: number;
    total?: number;
    amountDue?: number;
    amountPaid?: number;
    pointsEarned?: number;
    paymentMethod?: {
      type: string;
      last4?: string;
      cardType?: string;
    };
  };
}

// Claim OTA Booking interfaces
export interface CheckOtaReservationRequest {
  checkInDate: string;
  confirmationNumber: string;
  lastName: string;
  conversationId?: string;
  workflowSessionId?: string;
  agentName?: string;
}

export interface OtaReservationDetails {
  confirmationNumber: string;
  reservationId: string;
  guestName: string;
  email: string;
  phone: string;
  arrivalDate: string;
  departureDate: string;
  roomType: string;
  ratePlanCode: string;
  rateAmount: number;
  currency: string;
  adultCount: number;
  childCount: number;
  reservationStatus: string;
  paymentMethod: string;
  roomDetails?: {
    name?: string;
    featuredImage?: string;
    media?: Array<{
      url: string;
      isPrimary?: boolean;
    }>;
  };
}

export interface CheckOtaReservationResponse {
  success: boolean;
  message: string;
  canClaimOTABooking: boolean;
  reservationDetails?: OtaReservationDetails;
}

export interface ClaimOtaBookingRequest {
  agent: string;
  workflowSessionId: string;
  conversationId: string;
  reservationId: string;
  guestEmail: string;
}

export interface ClaimOtaBookingResponse {
  success: boolean;
  message: string;
  reservation?: {
    id: string;
    reservationId: string;
    confirmationNumber: string;
    guestEmail: string;
    arrivalDate: string;
    departureDate: string;
  };
  workflowResponse?: any;
}

export interface AssignRoomRequest {
  reservationId: string;
  roomNumber: string;
}

export interface AssignRoomResponse {
  success: boolean;
  message?: string;
  retryRoomFetch?: boolean;
  data?: {
    reservationId: string;
    roomNumber: string;
    message: string;
  };
}


// Export singleton instance
export const bookingService = new BookingService();
export default bookingService;